import gql from 'graphql-tag';

/**
 * @description Questionnaire and feedback fields
 */
const EVENT_QUESTION_FRAGMENT = gql`
  fragment EventQuestionFields on Event {
    preeventEnable
    questionnaireCutoffAt
    isPastQuestionnaireCutoff
    preEventQuestions {
      id
      label
      type
      options
      hasOtherOption
      isRequired
      isDeleted
      isAnswered
      responses
      properties {
        scaleMinValue
        scaleMaxValue
        scaleMinLabel
        scaleMaxLabel
      }
    }
    feedbackEnable
    isFeedbackSubmitted
    isFeedbackAnonymous
    postQuestions {
      id
      label
      type
      options
      hasOtherOption
      isRequired
      isDeleted
      isAnswered
      responses
      properties {
        scaleMinValue
        scaleMaxValue
        scaleMinLabel
        scaleMaxLabel
      }
    }
  }
`;

/**
 * @description Minimal basic event details
 * id, date, location, virtual, plus one config
 */
const EVENT_BASE_FIELDS = `
  id
  eventType
  title
  description
  locationName
  locationAddress
  datetimeScheduled
  datetimeEnd
  allDay
  timezoneCode
  hasStarted
  hasEnded
  isInPerson
  isVirtual
  numSpotsLeft
  plusOneConfig {
    eventId
    allowPlusOnes
    maxPlusOnes
    collectQuestionnaireResponses
  }
  virtualEventInfo {
    virtualLocation
    hasUnlimitedSpots
  }
  hasZoomMeeting
  hasGoogleMeeting
`;

const EVENT_BASE_FRAGMENT = gql`
  fragment EventBaseFields on Event { ${EVENT_BASE_FIELDS} }
`;
const SESSION_BASE_FRAGMENT = gql`
  fragment SessionBaseFields on EventSession { ${EVENT_BASE_FIELDS} }
`;

/**
 * @description Host organization and contact info
 */
const EVENT_ORGANIZER_FRAGMENT = gql`
  fragment EventOrganizerFields on Event {
    contactName
    contactEmail
    hostOrganization {
      id
      name
      aboutDescription
    }
    hostOrganizationName
  }
`;

/**
 * @description Guest totals and samples
 */
const EVENT_GUEST_DATA_FRAGMENT = gql`
  fragment EventGuestDataFields on Event {
    guestListTotals {
      confirmed
      waitlisted
      interested
    }
    confirmedUsersSample {
      id
      firstName
      lastName
      profilePictureUrl
      team
      role
    }
  }
`;

/**
 * @description Multi-session only fields
 * sessions, limitRegistration etc
 */
const MULTI_SESSION_FRAGMENT = gql`
  fragment MultiSessionFields on Event {
    limitRegistration
    sessions {
      ...SessionBaseFields
    }
  }
  ${SESSION_BASE_FRAGMENT}
`;

/**
 * @description Fields that only apply to
 * single events and multi-session parents
 */
const PARENT_EVENT_ONLY_FRAGMENT = gql`
  fragment ParentOnlyFields on Event {
    isThirtyDaysAfterEventEnded
    registrationCutoffAt
    isPastRegistrationCutoff
    isPrivate
    photoUrl
    slackPhotoUrl
    tags
    canSeeEventGuestList
    userInvited
    calendarNotifications
    scheduledDeletionAt
    isPublished
    isArchived
    companyLocation {
      id
      name
    }
  }
`;

export const EVENT_QUERY = gql`
  query Event($id: Int!) {
    event(id: $id) {
      ...EventBaseFields
      ...ParentOnlyFields
      ...EventOrganizerFields
      ...EventQuestionFields
      ...EventGuestDataFields
      ...MultiSessionFields
    }
    currentUser {
      id
      firstName
      lastName
      team
      companyId
      company {
        id
        name
        twitterHandle
      }
      match(eventId: $id) {
        gid
        userId
        eventId
        isAutoDeclined
        status
      }
      sessionMatches(eventId: $id) {
        eventId
        userId
        status
      }
    }
  }
  ${EVENT_BASE_FRAGMENT}
  ${PARENT_EVENT_ONLY_FRAGMENT}
  ${EVENT_ORGANIZER_FRAGMENT}
  ${EVENT_QUESTION_FRAGMENT}
  ${EVENT_GUEST_DATA_FRAGMENT}
  ${MULTI_SESSION_FRAGMENT}
`;

export const CHECKIN_PAGE_EVENT_QUERY = gql`
  query Event($id: Int!, $parentId: Int!) {
    event(id: $id) {
      id
      title
      hasEnded
      datetimeEnd
      estimatedAttendees
      isUsingEstimatedAttendees
      feedbackEnable
      timezoneCode
      virtualEventInfo {
        hasUnlimitedSpots
      }
    }
    parentEvent: event(id: $parentId) {
      id
      title
    }
  }
`;

export const CHECKIN_PAGE_PARENT_EVENT_QUERY = gql`
  query Event($id: Int!) {
    event(id: $id) {
      id
      feedbackEnable
      sessions {
        hasAttendedGuest
      }
    }
  }
`;

export const COMBINED_CHECKIN_PAGE_EVENT_QUERY = gql`
  query CombinedEventQuery($eventId: Int!, $parentId: Int!, $sessionId: Int!) {
    latestFeedbackRequest(eventId: $eventId) {
      id
      sendAt
      isPrimary
      isDeleted
      selectedGuestStatuses
      status
      sentAt
      responseCount
      createdByName
    }
    parentEvent: event(id: $parentId) {
      id
      feedbackEnable
      sessions {
        hasAttendedGuest
      }
    }
    eventSession: event(id: $sessionId) {
      id
      title
    }
  }
`;
